/**
 * Putting a block of reusable prose into a note that already exists.
 *
 * One function, used by both sides, for the reason `wallClockToInstant` is one
 * function: the server appends to a day's stored note and the client appends
 * into the item form Ana is typing in, and if those two ever disagreed about
 * spacing she would get a different result depending on where she inserted from.
 */

/**
 * Adds `addition` after whatever is already written, and never instead of it.
 *
 * Appending rather than replacing is the same rule PATTERNS §3d states for
 * copying a day: an insert that clears first destroys written work with one
 * mis-click, and there is no undo on a textarea that saves on blur.
 *
 * The blank line between the two is load-bearing, not tidiness. Notes are
 * rendered as Markdown with `remark-breaks`, so a single newline becomes a
 * `<br>` *inside the paragraph above* — the snippet arrives on the traveller's
 * page looking like the tail of a sentence Ana wrote about something else. It is
 * worse than cosmetic for block constructs: a GFM table cannot interrupt a
 * paragraph, so a snippet that opens with one is not recognised as a table at
 * all and renders as a row of pipes.
 */
export function appendToNotes(existing: string | null | undefined, addition: string): string {
  // Trailing whitespace is stripped from what is already there rather than
  // trusted, because a textarea that has been typed in usually ends in a
  // newline and two blank lines read as a gap somebody left on purpose.
  const kept = (existing ?? '').replace(/\s+$/, '')
  const added = addition.trim()
  if (kept === '') return added
  if (added === '') return kept
  return `${kept}\n\n${added}`
}
